import { useCallback } from "react";
import { addMessage, removeMessage, updateMessageStatus } from "@features/messages/messagesSlice";
import { sendFacebookMessage } from "@api/services/facebook";
import { sendZaloMessage } from "@api/services/zalo";
import { SOCKET_EVENTS } from "@constants/socket.events";
import { CHANNELS } from "@common";
import { useSocket } from "@hooks/useSocket";
import { getDisplayNameFromToken } from "@utils/tokenUtils";
import { useChatStore } from "@stores/chatStore";
import type { Message } from "@interfaces/chat.interface";
import type { AppDispatch } from "@stores/redux/store";

type Conversation = {
  id: string;
  channel: string;
  lastMessage?: Message;
  members?: Array<{ id: string }>;
  fanpageName?: string;
};

type Page = { id: string; name: string };
type MessagesStore = {
  messagesByConversation: Record<string, Message[]>;
};

type Attachment = {
  attachmentId: string;
  type: "image" | "video" | "file";
  fileName: string;
  fileUrl?: string;
};

type Params = {
  token?: string;
  activeConversation?: Conversation;
  currentConversationId: string | null;
  selectedFanpageName?: string;
  facebookPages: Page[];
  messagesStore: MessagesStore;
  dispatch: AppDispatch;
  showError: (title: string, message: string, duration?: number) => void;
};

export const useConversationSend = ({
  token,
  activeConversation,
  currentConversationId,
  selectedFanpageName,
  facebookPages,
  messagesStore,
  dispatch,
  showError,
}: Params) => {
  const { emit } = useSocket(token);

  const handleSend = useCallback(
    async (body: string, attachments?: Attachment[]) => {
      if (
        !activeConversation ||
        (!body.trim() && (!attachments || attachments.length === 0))
      ) {
        return;
      }

      const messageText = body.trim();
      const attachmentsList = attachments || [];

      if (activeConversation.channel === CHANNELS.ZALO) {
        const conversationId = activeConversation.id;

        if (!conversationId) {
          showError(
            "Không thể gửi tin nhắn",
            "Không tìm thấy thông tin hội thoại. Vui lòng thử lại sau khi tải lại trang hoặc chọn lại cuộc hội thoại."
          );
          return;
        }

        const actorSource = getDisplayNameFromToken(token || null);

        const sendSingleMessage = async (
          text: string | undefined,
          attachment: Attachment | undefined,
          index: number
        ) => {
          const tempMessageId = `temp-${Date.now()}-${Math.random()}-${index}`;
          const messageType: Message["type"] = attachment ? "image" : "text";

          const messageBody =
            attachment?.fileUrl ||
            text ||
            attachment?.fileName ||
            "";

          const sendingMessage: Message = {
            id: tempMessageId,
            conversationId: activeConversation.id,
            sender: {
              id: "current-user",
              name: "Bạn",
              avatar: "",
            },
            body: messageBody,
            createdAt: new Date().toISOString(),
            status: "sending",
            type: messageType,
            meta: {
              direction: "OUTBOUND",
              provider: "zalo",
              attachmentId: attachment?.attachmentId,
              previewUrl: attachment?.fileUrl,
              fileName: attachment?.fileName,
            },
          };

          dispatch(
            addMessage({
              conversationId: activeConversation.id,
              message: sendingMessage,
            })
          );

          useChatStore
            .getState()
            .addMessage(activeConversation.id, sendingMessage, true);

          try {
            const response = await sendZaloMessage({
              conversationId,
              type: attachment ? "image" : "text",
              text: attachment ? undefined : text || "",
              attachmentId: attachment?.attachmentId,
            });

            const finalMessage: Message = {
              ...sendingMessage,
              id: response.data?.messageId || tempMessageId,
              status: "sent",
              body: sendingMessage.body,
              meta: {
                ...sendingMessage.meta,
                ...(actorSource && { actorSource }),
              },
            };

            if (response.data?.messageId) {
              dispatch(
                removeMessage({
                  conversationId: activeConversation.id,
                  messageId: tempMessageId,
                })
              );
            } else {
              dispatch(
                updateMessageStatus({
                  conversationId: activeConversation.id,
                  messageId: tempMessageId,
                  status: "sent",
                })
              );
            }

            dispatch(
              addMessage({
                conversationId: activeConversation.id,
                message: finalMessage,
              })
            );

            useChatStore
              .getState()
              .addMessage(activeConversation.id, finalMessage, true);
          } catch (error) {
            dispatch(
              removeMessage({
                conversationId: activeConversation.id,
                messageId: tempMessageId,
              })
            );
            showError(
              "Lỗi gửi tin nhắn",
              error instanceof Error ? error.message : "Gửi tin nhắn thất bại",
              4
            );
          }
        };

        try {
          if (messageText) {
            await sendSingleMessage(messageText, undefined, 0);
          }

          if (attachmentsList.length > 0) {
            for (let i = 0; i < attachmentsList.length; i++) {
              if (i > 0 || messageText) {
                await new Promise((resolve) => setTimeout(resolve, 100));
              }
              await sendSingleMessage(undefined, attachmentsList[i], i);
            }
          }

          emit(SOCKET_EVENTS.MESSAGE_NEW, {
            conversationId: activeConversation.id,
            body: messageText,
          });
        } catch (error) {
          showError(
            "Lỗi gửi tin nhắn",
            error instanceof Error ? error.message : "Gửi tin nhắn thất bại",
            4
          );
        }

        return;
      }

      if (
        activeConversation.channel === CHANNELS.FACEBOOK &&
        selectedFanpageName
      ) {
        const selectedPage = facebookPages.find(
          (page) => page.name === selectedFanpageName
        );
        if (!selectedPage) return;

        const lastMessage = activeConversation.lastMessage;
        let recipientId: string | undefined;

        if (lastMessage?.meta?.direction === "INBOUND") {
          recipientId = lastMessage.sender.id;
        } else if (lastMessage?.meta?.receiverId) {
          recipientId = lastMessage.meta.receiverId as string;
        } else if (currentConversationId) {
          const messages =
            messagesStore.messagesByConversation[currentConversationId] || [];
          const message =
            messages.find(
              (msg) => msg.meta?.direction === "INBOUND" && msg.sender?.id
            ) || messages.find((msg) => msg.meta?.receiverId);
          recipientId =
            message?.meta?.direction === "INBOUND"
              ? message.sender.id
              : (message?.meta?.receiverId as string | undefined);
        }

        if (!recipientId) {
          const member = activeConversation.members?.find(
            (m) => m.id !== selectedPage.id
          );
          recipientId = member?.id;
        }

        if (!recipientId) {
          showError(
            "Không thể gửi tin nhắn",
            "Không tìm thấy thông tin người nhận. Vui lòng thử lại sau khi tải lại trang hoặc chọn lại cuộc hội thoại."
          );
          return;
        }

        const sendSingleMessage = async (
          text: string | undefined,
          attachment: Attachment | undefined,
          index: number
        ) => {
          const tempMessageId = `temp-${Date.now()}-${Math.random()}-${index}`;
          const messageType: Message["type"] =
            attachment?.type === "image"
              ? "image"
              : attachment?.type === "video"
              ? "video"
              : attachment
              ? "file"
              : "text";

          const messageBody =
            attachment?.type === "image" && attachment.fileUrl
              ? attachment.fileUrl
              : attachment?.type === "video" && attachment.fileUrl
              ? attachment.fileUrl
              : text || attachment?.fileName || "";

          const sendingMessage: Message = {
            id: tempMessageId,
            conversationId: activeConversation.id,
            sender: {
              id: "current-user",
              name: "Bạn",
              avatar: "",
            },
            body: messageBody,
            createdAt: new Date().toISOString(),
            status: "sending",
            type: messageType,
            meta: {
              direction: "OUTBOUND",
              attachmentId: attachment?.attachmentId,
              previewUrl: attachment?.fileUrl,
              fileName: attachment?.fileName,
            },
          };

          dispatch(
            addMessage({
              conversationId: activeConversation.id,
              message: sendingMessage,
            })
          );

          useChatStore
            .getState()
            .addMessage(activeConversation.id, sendingMessage, true);

          try {
            const response = await sendFacebookMessage({
              pageId: selectedPage.id,
              recipientId: recipientId,
              text: text || undefined,
              attachmentId: attachment?.attachmentId,
              attachmentType: attachment?.type,
            });

            if (response.code === -1001) {
              throw new Error(response.message || "Gửi tin nhắn thất bại");
            }

            const actorSource = getDisplayNameFromToken(token || null);
            
            const finalMessage: Message = {
              ...sendingMessage,
              id: response.data?.messageId || tempMessageId,
              status: "sent",
              body: sendingMessage.body,
              meta: {
                ...sendingMessage.meta,
                previewUrl: sendingMessage.meta?.previewUrl,
                ...(actorSource && { actorSource }),
              },
            };

            if (response.data?.messageId) {
              dispatch(
                removeMessage({
                  conversationId: activeConversation.id,
                  messageId: tempMessageId,
                })
              );
            } else {
              dispatch(
                updateMessageStatus({
                  conversationId: activeConversation.id,
                  messageId: tempMessageId,
                  status: "sent",
                })
              );
            }

            dispatch(
              addMessage({
                conversationId: activeConversation.id,
                message: finalMessage,
              })
            );

            useChatStore
              .getState()
              .addMessage(activeConversation.id, finalMessage, true);
          } catch (error) {
            dispatch(
              removeMessage({
                conversationId: activeConversation.id,
                messageId: tempMessageId,
              })
            );
            showError(
              "Lỗi gửi tin nhắn",
              error instanceof Error ? error.message : "Gửi tin nhắn thất bại",
              4
            );
          }
        };

        try {
          if (messageText) {
            await sendSingleMessage(messageText, undefined, 0);
          }

          if (attachmentsList.length > 0) {
            for (let i = 0; i < attachmentsList.length; i++) {
              if (i > 0 || messageText) {
                await new Promise((resolve) => setTimeout(resolve, 100));
              }
              await sendSingleMessage(undefined, attachmentsList[i], i);
            }
          }

          emit(SOCKET_EVENTS.MESSAGE_NEW, {
            conversationId: activeConversation.id,
            body: messageText,
          });
        } catch (error) {
          showError(
            "Lỗi gửi tin nhắn",
            error instanceof Error ? error.message : "Gửi tin nhắn thất bại",
            4
          );
        }
      } else {
        emit(SOCKET_EVENTS.MESSAGE_NEW, {
          conversationId: activeConversation.id,
          body,
        });
      }
    },
    [
      activeConversation,
      currentConversationId,
      selectedFanpageName,
      facebookPages,
      messagesStore.messagesByConversation,
      dispatch,
      showError,
      token,
      emit,
    ]
  );

  return { handleSend };
};
